import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import { colors } from "../../theme/colors";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import { formatMapName } from "../../constants/maps";
import { normalizeMapSelectionToMapId } from "./lobbyPageUtils";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import { MOBILE_NAV_MQ } from "./lobbyPageStyles";
import MapThumbCard from "../common/MapThumbCard";

const MAX_VISIBLE_SLOTS = 7;
const CARD_WIDTH_PX = 100;
const CARD_WIDTH_MOBILE_PX = 84;
const CARD_HEIGHT = "3.75rem";
const CARD_HEIGHT_MOBILE = "2.75rem";
const CARD_GAP_PX = 14;

function resolveMapEntry(selection) {
    const mapId = normalizeMapSelectionToMapId(selection);
    if (!mapId) return null;
    const meta = getMapMetaById(mapId);
    return {
        key: mapId,
        mapId,
        label: meta?.display || formatMapName(selection),
        imageUrl: getMapImageUrl(mapId),
    };
}

function MapPoolCard({ label, imageUrl }) {
    return (
        <MapThumbCard
            imageUrl={imageUrl}
            label={label}
            checked
            compact
        />
    );
}

MapPoolCard.propTypes = {
    label: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
};

function OverflowCard({ count }) {
    return (
        <Box
            aria-label={`${count} more maps`}
            sx={{
                flex: "0 0 auto",
                width: `${CARD_WIDTH_PX}px`,
                height: CARD_HEIGHT,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                clipPath:
                    "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)",
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.045),
                    inset 0 -1px 0 rgba(0,0,0,0.55)
                `,
                background: `
                    linear-gradient(
                        165deg,
                        ${alpha(colors.background.card, 0.55)} 0%,
                        ${alpha(colors.background.dark, 0.85)} 100%
                    )
                `,
                [MOBILE_NAV_MQ]: {
                    width: `${CARD_WIDTH_MOBILE_PX}px`,
                    height: CARD_HEIGHT_MOBILE,
                },
            }}
        >
            <Typography
                sx={{
                    color: alpha(colors.text.primary, 0.92),
                    fontSize: "0.95rem",
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    [MOBILE_NAV_MQ]: { fontSize: "0.85rem" },
                }}
            >
                +{count}
            </Typography>
        </Box>
    );
}

OverflowCard.propTypes = {
    count: PropTypes.number.isRequired,
};

const MAP_POOL_LABEL_COLOR = alpha(colors.accent.soft, 0.58);

const MAP_POOL_TITLE_SX = {
    fontSize: { xs: "0.68rem", xl: "0.72rem" },
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: MAP_POOL_LABEL_COLOR,
    lineHeight: 1,
    [MOBILE_NAV_MQ]: {
        fontSize: "0.62rem",
        letterSpacing: "0.14em",
    },
};

function MapPoolHeader({ end = null }) {
    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    minWidth: 0,
                }}
            >
                <LayersOutlined
                    aria-hidden
                    sx={{
                        fontSize: { xs: "0.95rem", xl: "1.05rem" },
                        color: MAP_POOL_LABEL_COLOR,
                        flexShrink: 0,
                    }}
                />
                <Typography sx={MAP_POOL_TITLE_SX}>Map Pool</Typography>
            </Box>
            {end}
        </Box>
    );
}

MapPoolHeader.propTypes = {
    end: PropTypes.node,
};

/**
 * Responsive selected-map strip under PLAY.
 * Shows up to 7 map cards; overflow collapses into a dashed +N card.
 */
export default function LobbyMapPool({
    selectedMaps = [],
    compact = false,
    headerEnd = null,
}) {
    const trackRef = useRef(null);
    const [slotCapacity, setSlotCapacity] = useState(MAX_VISIBLE_SLOTS);

    const maps = useMemo(() => {
        const seen = new Set();
        const next = [];
        (selectedMaps || []).forEach((selection) => {
            const entry = resolveMapEntry(selection);
            if (!entry || seen.has(entry.key)) return;
            seen.add(entry.key);
            next.push(entry);
        });
        return next;
    }, [selectedMaps]);

    useEffect(() => {
        const node = trackRef.current;
        if (!node || typeof ResizeObserver === "undefined") return undefined;

        const measure = () => {
            const width = node.clientWidth || 0;
            const cardW = window.matchMedia(MOBILE_NAV_MEDIA).matches
                ? CARD_WIDTH_MOBILE_PX
                : CARD_WIDTH_PX;
            const fit = Math.max(
                1,
                Math.floor((width + CARD_GAP_PX) / (cardW + CARD_GAP_PX)),
            );
            setSlotCapacity(Math.min(MAX_VISIBLE_SLOTS, fit));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        window.addEventListener("resize", measure);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, []);

    if (maps.length === 0) {
        if (!compact) return null;

        return (
            <Box
                className="lobbyMapPool lobbyMapPool--empty"
                sx={{
                    width: "100%",
                    minHeight: CARD_HEIGHT,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "center",
                    gap: "0.55rem",
                    px: "0.35rem",
                    boxSizing: "border-box",
                }}
            >
                <MapPoolHeader end={headerEnd} />
                <Typography
                    sx={{
                        fontSize: { xs: "0.72rem", xl: "0.76rem" },
                        fontWeight: 600,
                        color: alpha(colors.text.muted, 0.82),
                        lineHeight: 1.35,
                    }}
                >
                    No maps selected — edit setup to continue
                </Typography>
            </Box>
        );
    }

    const needsOverflow = maps.length > slotCapacity;
    const visibleCount = needsOverflow
        ? Math.max(0, slotCapacity - 1)
        : maps.length;
    const overflowCount = maps.length - visibleCount;
    const visibleMaps = maps.slice(0, visibleCount);

    return (
        <Box
            className="lobbyMapPool"
            sx={{
                width: "100%",
                maxWidth: compact ? "100%" : "72rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: compact
                    ? { xs: "0.45rem", xl: "0.5rem" }
                    : { xs: "0.55rem", xl: "0.65rem" },
                px: "0.35rem",
                boxSizing: "border-box",
                [MOBILE_NAV_MQ]: {
                    gap: "0.45rem",
                },
            }}
        >
            <MapPoolHeader end={headerEnd} />

            <Box
                ref={trackRef}
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    flexWrap: "nowrap",
                    gap: `${CARD_GAP_PX}px`,
                    // Clip horizontally only — vertical overflow stays open for cradle bloom
                    overflowX: "hidden",
                    overflowY: "visible",
                    pb: "0.2rem",
                    boxSizing: "border-box",
                }}
            >
                {visibleMaps.map((map) => (
                    <MapPoolCard
                        key={map.key}
                        label={map.label}
                        imageUrl={map.imageUrl}
                    />
                ))}
                {overflowCount > 0 && <OverflowCard count={overflowCount} />}
            </Box>
        </Box>
    );
}

LobbyMapPool.propTypes = {
    selectedMaps: PropTypes.arrayOf(PropTypes.string),
    compact: PropTypes.bool,
    headerEnd: PropTypes.node,
};
